import React, { Fragment } from "react";
import PropTypes from "prop-types";
import "../index.css";
import classNames from "classnames";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import Button from "./Button";

const Modal = ({
  title,
  description,
  type,
  primaryCta,
  primaryOnClick,
  secondaryCta,
  secondaryOnClick,
  dismissButton,
  dismissButtonOnClick,
  buttonBackgroundColor,
  buttonsLeftAligned,
  centered,
  icon,
  open,
  primaryCtaLoading,

  ...rest
}) => {
  const containerStyle = !buttonBackgroundColor ? "px-4 pt-5 pb-4 sm:p-6" : "";

  const innerContainerStyle = buttonBackgroundColor
    ? "bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4"
    : "";

  const buttonAlignmentStyle =
    !buttonsLeftAligned && !centered
      ? "sm:flex-row-reverse"
      : centered && secondaryCta
      ? "mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
      : centered && !secondaryCta
      ? ""
      : "sm:ml-10 sm:pl-4";

  const buttonContainerStyle = buttonBackgroundColor
    ? `${buttonBackgroundColor} px-4 py-3 sm:px-6`
    : !centered
    ? "mt-5 sm:mt-4"
    : centered && !secondaryCta
    ? "mt-5 sm:mt-6"
    : "";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-10 inset-0 overflow-y-auto"
        open={open}
        onClose={dismissButtonOnClick}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={classNames(
                "inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full",
                containerStyle
              )}
            >
              {dismissButton ? (
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    onClick={dismissButtonOnClick}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                ""
              )}
              <div className={innerContainerStyle}>
                <div
                  className={classNames(
                    !centered ? "sm:flex sm:items-start" : ""
                  )}
                >
                  <div
                    className={classNames(
                      "mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full  ",
                      !centered ? "sm:mx-0 sm:h-10 sm:w-10" : "",
                      type === "warning" ? "bg-red-100" : "bg-green-100"
                    )}
                  >
                    {icon?.type && (
                      <icon.type
                        className={classNames(
                          "h-6 w-6 ",
                          type === "warning" ? "text-red-600" : "text-green-600"
                        )}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div
                    className={classNames(
                      "mt-3 text-center ",
                      !centered ? "sm:mt-0 sm:ml-4 sm:text-left" : ""
                    )}
                  >
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      {title}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{description}</p>
                    </div>
                  </div>
                </div>
              </div>
              {primaryCta || secondaryCta ? (
                <div
                  className={classNames(
                    "sm:flex ",
                    buttonContainerStyle,
                    buttonAlignmentStyle
                  )}
                >
                  {primaryCta ? (
                    <Button
                      className={classNames(
                        "w-full",
                        !buttonsLeftAligned && !centered ? "sm:ml-3" : "",
                        centered && !secondaryCta ? "" : "sm:w-auto"
                      )}
                      onClick={primaryOnClick}
                      loading={primaryCtaLoading}
                    >
                      <span>{primaryCta}</span>
                    </Button>
                  ) : (
                    ""
                  )}

                  {secondaryCta ? (
                    <Button
                      className={classNames(
                        "w-full sm:w-auto mt-3 sm:mt-0",
                        buttonsLeftAligned ? "sm:ml-3" : ""
                      )}
                      buttonStyle="white"
                      onClick={secondaryOnClick}
                    >
                      <span>{secondaryCta}</span>
                    </Button>
                  ) : (
                    ""
                  )}
                </div>
              ) : (
                ""
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

Modal.defaultProps = {
  dismissButton: false,
  buttonsLeftAligned: false,
  centered: false,
};

Modal.propTypes = {
  /**
   *Title
   */
  title: PropTypes.string,

  /**
   * Description
   */
  description: PropTypes.string,

  /**
   * Type
   */
  type: PropTypes.oneOf(["success", "warning"]),

  /**
   * Primary CTA
   */
  primaryCta: PropTypes.string,

  /**
   * Primary Button OnClick Action
   */
  primaryOnClick: PropTypes.func,

  /**
   * Secondary CTA
   */
  secondaryCta: PropTypes.string,

  /**
   * Secondary Button OnClick Action
   */
  secondaryOnClick: PropTypes.func,

  /**
   * Dismiss Button
   */
  dismissButton: PropTypes.bool,

  /**
   * Button Background Color
   */
  buttonBackgroundColor: PropTypes.string,

  /**
   *  Button Alignment
   */
  buttonsLeftAligned: PropTypes.bool,

  /**
   * Content Center
   */
  centered: PropTypes.bool,

  /**
   * Icon
   */
  icon: PropTypes.node,
};

export default Modal;
